/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { Typography } from '@mui/material'
import { usePatientTask } from '@om1/aspen-api'
import { formatDate } from '@om1/platform-components'
import { startCase } from 'lodash'
import { Check, Clock, FileText, User, X } from 'lucide-react'
import React from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { GenericDetailTable } from '../components/GenericDetailTable'
import LoadingComponent from '../components/LoadingComponent'
import GenericDetailPage from './GenericDetailPage'

// Status badge component for better visual representation
const StatusBadge = ({ status }: { status: string }) => {
    let bgColor = 'bg-gray-100'
    let textColor = 'text-gray-700'
    let icon: React.ReactNode | null = null
    let description = ''

    // Map status to appropriate colors, icons, and descriptions
    if (status === 'FAILURE') {
        bgColor = 'bg-red-100'
        textColor = 'text-red-800'
        icon = <X size={16} className='mr-1' />
        description = 'Task failed'
    } else if (status === 'SUCCESS') {
        bgColor = 'bg-green-100'
        textColor = 'text-green-800'
        icon = <Check size={16} className='mr-1' />
        description = 'Task completed successfully'
    } else if (status === 'PENDING') {
        bgColor = 'bg-blue-100'
        textColor = 'text-blue-800'
        icon = <Clock size={16} className='mr-1' />
        description = 'Task is pending'
    } else if (status === 'STARTED') {
        bgColor = 'bg-yellow-100'
        textColor = 'text-yellow-800'
        icon = <Clock size={16} className='mr-1' />
        description = 'Task has started'
    } else {
        icon = <Clock size={16} className='mr-1' />
    }

    const displayStatus = status.replace(/_/g, ' ').toLowerCase()

    return (
        <div
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${bgColor} ${textColor} text-sm font-medium shadow-sm`}
            title={description || displayStatus}
        >
            {icon}
            <span className='capitalize'>{displayStatus}</span>
        </div>
    )
}

// Styled action button component
const ActionButton = ({
    icon,
    label,
    onClick,
    className = '',
    primary = false
}: {
    icon: React.ReactNode
    label: string
    onClick: () => void
    className?: string
    primary?: boolean
}) => {
    const baseClasses =
        'flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md transition-colors text-sm font-medium min-w-[140px] shadow-sm h-[36px]'
    const primaryClasses = 'bg-primary-main text-white hover:bg-primary-dark'
    const secondaryClasses = 'bg-white border border-gray-200 text-primary-main hover:bg-gray-50'

    return (
        <button onClick={onClick} className={`${baseClasses} ${primary ? primaryClasses : secondaryClasses} ${className}`}>
            {icon}
            <span>{label}</span>
        </button>
    )
}

// Component to display task information in a clean, consistent layout
const TaskInfoSection = ({
    title,
    icon,
    children,
    className = '',
    actionButton
}: {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
    className?: string
    actionButton?: React.ReactNode
}) => {
    return (
        <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm ${className}`}>
            <div className='flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50'>
                <div className='text-primary-main'>{icon}</div>
                <h3 className='text-base font-semibold text-gray-800 m-0'>{title}</h3>
            </div>
            <div className='p-4'>
                {actionButton}
                {children}
            </div>
        </div>
    )
}

const TaskDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { task, loading, error } = usePatientTask(id)
    const history = useHistory()

    const taskTitleName = startCase(task?.task?.name?.split('.').pop()?.replaceAll('_', ' ') || '')

    const title = (
        <div className='flex-1 flex items-center'>
            <div className='flex items-center mr-4'>
                <div className='bg-primary-main text-white p-1.5 rounded-full flex-shrink-0 mr-3'>
                    <FileText size={20} />
                </div>
                <div className='min-w-0'>
                    <Typography variant='h6' className='font-semibold truncate'>
                        {taskTitleName}
                    </Typography>
                    <div className='flex items-center gap-2 mt-1'>
                        <span className='text-xs text-gray-500'>ID: {task?.taskId}</span>
                    </div>
                </div>
            </div>
            {error && (
                <Typography color='error' className='ml-auto text-sm'>
                    {error}
                </Typography>
            )}
        </div>
    )

    if (!task || loading) {
        return (
            <div className='fixed inset-0 flex flex-col justify-center items-center'>
                <LoadingComponent />
            </div>
        )
    }

    return (
        <GenericDetailPage
            backButtonText='To Tasks'
            title={title}
            data={task}
            backRoute='/tasks'
            columns={[
                { key: 'patientId', type: 'text' },
                { key: 'task.status', type: 'text' },
                { key: 'taskCreatedDttm', type: 'date' }
            ]}
            additionalComponents={[
                <div key='content-layout' className='grid grid-cols-1 md:grid-cols-12 gap-4 pb-16'>
                    <div className='md:col-span-12'>
                        <TaskInfoSection title='Task Details' icon={<FileText size={18} />}>
                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                <div className='mb-3 last:mb-0'>
                                    <p className='text-xs text-gray-500 mb-0.5'>Patient</p>
                                    <ActionButton
                                        icon={<User className='w-4 h-4' />}
                                        label='View Patient'
                                        onClick={() => history.push(`/patients/${task.patientId}`)}
                                    />
                                </div>
                                <div className='mb-3 last:mb-0'>
                                    <p className='text-xs text-gray-500 mb-0.5'>Task ID</p>
                                    <p className='text-sm font-medium'>{task.taskId || 'N/A'}</p>
                                </div>
                                <div className='mb-3 last:mb-0'>
                                    <p className='text-xs text-gray-500 mb-0.5'>Status</p>
                                    {task.task.status ? <StatusBadge status={task.task.status} /> : 'N/A'}
                                </div>
                                <div className='mb-3 last:mb-0'>
                                    <p className='text-xs text-gray-500 mb-0.5'>Created At</p>
                                    <p className='text-sm font-medium'>{formatDate(task.taskCreatedDttm) || 'N/A'}</p>
                                </div>
                            </div>
                        </TaskInfoSection>
                    </div>

                    <div className='md:col-span-12'>
                        <TaskInfoSection title='Task Result' icon={<FileText size={18} />}>
                            <GenericDetailTable
                                data={{ data: [task.task.result] }}
                                columns={Object.keys(task.task.result)}
                                className='max-h-[300px] overflow-y-auto'
                                stickyHeader={true}
                            />
                        </TaskInfoSection>
                    </div>
                </div>
            ]}
        />
    )
}

export default TaskDetailPage
